import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  FiFile,
  FiFolder,
  FiChevronRight,
  FiChevronDown,
  FiTrash2,
  FiEdit2,
  FiCopy,
  FiMoreVertical
} from 'react-icons/fi'
import { VscFolder, VscFolderOpened } from 'react-icons/vsc'
import { FaFire } from 'react-icons/fa'

const FileTree = ({ files, currentFile, onFileSelect, onFileDelete, expandedFolders, onToggleFolder }) => {
  const [editingFile, setEditingFile] = useState(null)
  const [editName, setEditName] = useState('')
  const [showMenu, setShowMenu] = useState(null)

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase()
    const icons = {
      js: '🟨', ts: '🔷', jsx: '⚛️', tsx: '⚛️',
      html: '🌐', css: '🎨', json: '📋',
      py: '🐍', java: '☕', cpp: '⚙️', c: '⚙️',
      php: '🐘', rb: '💎', go: '🚀', rs: '🦀',
      md: '📝', txt: '📄', lock: '🔒',
    }
    return icons[ext] || '📄'
  }

  const getFilesByPath = (path) => {
    return Object.values(files).filter(file => 
      file.path === path && file.type === 'file'
    )
  }

  const getSubfolders = (path) => {
    const folders = new Set()
    Object.values(files).forEach(file => {
      if (file.path.startsWith(path + '/') && file.path !== path) {
        const nextPath = file.path.substring(0, file.path.indexOf('/', path.length + 1))
        if (nextPath !== -1) {
          folders.add(nextPath)
        }
      }
    })
    return Array.from(folders)
  }

  const handleFileClick = (file) => {
    onFileSelect(file)
    setShowMenu(null)
  }

  const handleRename = (file) => {
    if (!editName.trim()) return
    
    const updatedFile = { ...file, name: editName }
    onFileSelect(updatedFile)
    setEditingFile(null)
  }

  const renderTree = (path = '/', depth = 0) => {
    const isExpanded = expandedFolders.includes(path)
    const subfolders = getSubfolders(path)
    const filesInPath = getFilesByPath(path)

    if (path !== '/' && subfolders.length === 0 && filesInPath.length === 0) {
      return null
    }

    return (
      <div className="select-none">
        {path !== '/' && (
          <div
            className="flex items-center py-1.5 px-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg cursor-pointer group"
            style={{ paddingLeft: `${depth * 16 + 8}px` }}
            onClick={() => onToggleFolder(path)}
          >
            <div className="flex items-center space-x-2 flex-1">
              {isExpanded ? (
                <FiChevronDown className="text-gray-500 dark:text-gray-400 flex-shrink-0" />
              ) : (
                <FiChevronRight className="text-gray-500 dark:text-gray-400 flex-shrink-0" />
              )}
              {isExpanded ? (
                <VscFolderOpened className="text-primary-600 dark:text-primary-400 flex-shrink-0" />
              ) : (
                <VscFolder className="text-primary-600 dark:text-primary-400 flex-shrink-0" />
              )}
              <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {path.split('/').pop() || 'Root'}
              </span>
            </div>
          </div>
        )}

        {isExpanded && (
          <div>
            {subfolders.map((subfolder) => (
              <div key={subfolder}>
                {renderTree(subfolder, depth + 1)}
              </div>
            ))}

            {filesInPath.map((file) => (
              <motion.div
                key={file.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className={`py-1.5 px-2 rounded-lg cursor-pointer group flex items-center justify-between ${
                  currentFile?.id === file.id
                    ? 'bg-primary-100 dark:bg-primary-900/30 border-l-4 border-primary-500'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
                style={{ paddingLeft: `${(depth + 1) * 16 + 8}px` }}
                onClick={() => handleFileClick(file)}
              >
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  <span className="text-lg">{getFileIcon(file.name)}</span>
                  {editingFile?.id === file.id ? (
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onBlur={() => handleRename(file)}
                      onKeyDown={(e) => e.key === 'Enter' && handleRename(file)}
                      className="flex-1 bg-transparent border-b border-primary-500 focus:outline-none text-sm text-gray-900 dark:text-white"
                      autoFocus
                    />
                  ) : (
                    <span className={`text-sm truncate ${
                      currentFile?.id === file.id
                        ? 'text-primary-700 dark:text-primary-300 font-semibold'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {file.name}
                    </span>
                  )}
                </div>

                <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {showMenu === file.id ? (
                    <div className="absolute right-4 bg-white dark:bg-gray-800 shadow-lg rounded-lg p-1 z-10 border border-gray-200 dark:border-gray-700">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setEditingFile(file)
                          setEditName(file.name)
                          setShowMenu(null)
                        }}
                        className="flex items-center space-x-2 w-full px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-sm"
                      >
                        <FiEdit2 />
                        <span>Rename</span>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          onFileDelete(file.id)
                          setShowMenu(null)
                        }}
                        className="flex items-center space-x-2 w-full px-3 py-2 hover:bg-red-500/10 text-red-600 dark:text-red-400 rounded text-sm"
                      >
                        <FiTrash2 />
                        <span>Delete</span>
                      </button>
                    </div>
                  ) : null}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowMenu(showMenu === file.id ? null : file.id)
                    }}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                  >
                    <FiMoreVertical className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-1">
      {Object.keys(files).length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-primary-100 to-red-100 dark:from-gray-800 dark:to-primary-900/20 flex items-center justify-center">
            <FaFire className="text-2xl text-primary-600 dark:text-primary-400 animate-pulse" />
          </div>
          <p className="text-sm">No files yet</p>
          <p className="text-xs mt-1">Create your first file to get started</p>
        </div>
      ) : (
        renderTree()
      )}
    </div>
  )
}

export default FileTree